import {
  stopIntercepting,
  tryLoadAndStartRecorder,
} from '@alwaysmeticulous/recorder-loader';
import {
  asterixOut,
  dropRequestHeader,
  redactRecursively,
  transformJsonResponse,
} from '@alwaysmeticulous/redaction';
import type {RecorderMiddleware} from '@alwaysmeticulous/sdk-bundles-api';
import {useEffect, useRef} from 'react';

import {envIsDev, envIsProd} from '../../config';
import {Meticulous} from '../../services/meticulous/Meticulous';
import {useViewer} from '../../state/viewer/hooks';
import {isInternalUser, useIsAdminModeActiveAndIsNotLocalEnv} from '../admin';
import {getDatadogRum} from '../datadog/datadogRum';
import {keyExistsInQueryString} from '../query-string';
import {redactPayloadField, SENSITIVE_KEYS} from '../sensitiveFieldKeys';

// Devs can opt out per page load by appending `?DISABLE_METICULOUS` to the URL.
// This is useful when trying to profile for performance since Meticulous gets noisy.
const DISABLE_METICULOUS_QS_KEY = 'DISABLE_METICULOUS';

function isMeticulousExplicitlyDisabled(): boolean {
  return keyExistsInQueryString(DISABLE_METICULOUS_QS_KEY);
}

// Members as of 9/16/2025
// wandb - 547
// coreweave_ml-org - 44
// coreweave1-org - 135
const ApprovedAccountList = [
  'wandb',
  'coreweave_ml-org',
  'coreweave1-org',
  'public-team-private-projects-org',
];

type RecorderStatus = 'not-started' | 'start-requested' | 'ready';

const DD_RUM = getDatadogRum();

// safe to store the token here since it's a FE token and there's no real way to obscure it
const token = 'YwJGtM8NRyAqBYb2dEhZnTZ9QzATNGpVBeLQBqhk';

const meticulousMiddleware = [
  dropRequestHeader('Authorization'),

  transformJsonResponse({
    urlRegExp: /.*/,
    transform: (data: object): object =>
      redactRecursively(data as Record<string, unknown>, {
        redactString: (str, path) => {
          const fieldName = path[path.length - 1];
          if (typeof fieldName === 'string' && SENSITIVE_KEYS.has(fieldName)) {
            return asterixOut(str);
          }
          return str;
        },
      }) as object,
  }),

  {
    transformNetworkRequest: (
      ...args: Parameters<
        NonNullable<RecorderMiddleware['transformNetworkRequest']>
      >
    ): ReturnType<
      NonNullable<RecorderMiddleware['transformNetworkRequest']>
    > => {
      const [request] = args;
      const text = request.postData?.text;
      if (!text) {
        return request;
      }
      const redacted = redactPayloadField(text);
      if (redacted === text) {
        return request;
      }
      return {
        ...request,
        postData: {...request.postData!, text: redacted!},
      };
    },
    applyRequestTransformationAtReplayTime: false,
  } as RecorderMiddleware,
];

// Mirror the logged-in viewer into the Meticulous session so recordings are
// searchable by user. Every call is a safe no-op until the recorder has
// loaded, so this is fire-and-forget. See src/services/meticulous/Meticulous.ts.
function recordViewerSessionContext({
  viewerId,
  viewerEmail,
}: {
  viewerId: string | undefined;
  viewerEmail: string | undefined;
}): void {
  Meticulous.recordUserId(viewerId);
  Meticulous.recordUserEmail(viewerEmail);
}

function useMeticulousRecorderDev(accountName?: string) {
  const viewer = useViewer();
  const viewerId = viewer?.id;
  const viewerEmail = viewer?.email;
  const viewerExists = !!viewer;
  const adminModeActive = useIsAdminModeActiveAndIsNotLocalEnv();
  const isExternal = viewer && !isInternalUser(viewer);
  const recorderStatus = useRef<RecorderStatus>('not-started');

  // We do not want the recorder to start with a stale viewer identity. We want
  // it to record the viewer who is current when loading finishes. A ref lets
  // the async callback read the latest value without triggering renders.
  const latestViewerIdentity = useRef({viewerId, viewerEmail});
  latestViewerIdentity.current = {viewerId, viewerEmail};

  // Track account names used by internal users so that we can audit them and find
  // more accounts that we can approve for recording meticulous sessions
  useEffect(() => {
    if (isExternal || !viewerExists) {
      return;
    }

    if (accountName && !!(viewer?.email ?? '')) {
      DD_RUM.addAction('log_internal_user_account_name', {
        accountName,
        email: viewer?.email,
        isAllowedAccount: ApprovedAccountList.includes(
          accountName.toLowerCase()
        ),
      });
    }
  }, [viewerExists, isExternal, accountName, viewer?.email, viewer]);

  useEffect(() => {
    // we don't need to worry about the hook in CI, we can record everything
    if (!viewerExists || !accountName) {
      return;
    }

    if (isMeticulousExplicitlyDisabled()) {
      // Honor the dev override before doing anything else so we never start
      // the recorder and we stop any in-flight network interception.
      stopIntercepting();
      console.log(
        'Stopped meticulous recorder via DISABLE_METICULOUS dev override'
      );
      return;
    }

    if (adminModeActive || isExternal) {
      stopIntercepting();
      if (adminModeActive) {
        console.log('Stopped meticulous recorder in admin mode');
      } else if (isExternal) {
        console.log('Stopped meticulous recorder in external mode');
      }
    } else if (
      recorderStatus.current === 'not-started' &&
      ApprovedAccountList.includes(accountName)
    ) {
      // the network recorder struggles over a certain size, this limits it.
      window.METICULOUS_MAX_PAYLOAD_SIZE = 50_000_000; // 50MB

      // we try to avoid multiple inits, but it's fine if this calls multiple times
      // it no-ops if active
      // https://weightsandbiases.slack.com/archives/C09BXRYRU2K/p1757978656392079?thread_ts=1757976730.837009&cid=C09BXRYRU2K
      tryLoadAndStartRecorder({
        recordingToken: token,
        isProduction: envIsProd,
        middleware: meticulousMiddleware,
      })
        .then(() => {
          // Record the viewer once the recorder is live so the initial context
          // isn't dropped while the snippet is still loading. Read the latest
          // identity from the ref (not the closure) so an identity change during
          // the load is reflected. Once `ready`, the sync effect below keeps it
          // current.
          recorderStatus.current = 'ready';
          recordViewerSessionContext(latestViewerIdentity.current);
        })
        .catch(() => {
          // Reset so a later effect run (e.g. switching accounts) can retry.
          // Otherwise the `not-started` guard above would never pass again and
          // the recorder, plus the viewer-identity sync gated on `ready`, would
          // stay dead for the rest of the session.
          recorderStatus.current = 'not-started';
          console.warn('Failed to start meticulous recorder');
        });

      console.log('Started meticulous recorder');
      recorderStatus.current = 'start-requested';
    }
  }, [accountName, adminModeActive, isExternal, viewerExists]);

  // Keep the identity in sync once the recorder is running (e.g. the viewer
  // logs in or switches accounts mid-session). Gated on `ready` so changes
  // during the load are handled by the start callback above instead; this
  // effect takes over for every change afterwards.
  useEffect(() => {
    if (recorderStatus.current !== 'ready') {
      return;
    }
    recordViewerSessionContext({viewerId, viewerEmail});
  }, [viewerId, viewerEmail]);
}

// No-op the meticulous recorder outside of allowed environments
// right now [9/26/2025s] we're only allowing the recorder in dev environments
export const useMeticulousRecorder = envIsDev
  ? useMeticulousRecorderDev
  : () => {};
